import { ReactElement, ReactNode, Children, isValidElement, cloneElement } from 'react';

interface GridRowProps {
  children: ReactNode;
  className?: string;
  runRowStyle?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export default function GridRow({
  children,
  className = '',
  runRowStyle = 'cr-border-solid cr-border-cirrusBlue-100 cr-border-b-2 cr-px-1 cr-py-1 cr-min-h-[2rem] cr-flex cr-items-center',
  onClick,
}: GridRowProps): ReactElement {
  // Count the number of valid child elements, excluding conditional elements that don't render
  const childrenArray = Children.toArray(children);
  const childCount = childrenArray.filter((child) => {
    // Include all valid React elements (including empty divs)
    if (isValidElement(child)) {
      return true;
    }
    // Include strings and numbers
    if (typeof child === 'string' || typeof child === 'number') {
      return true;
    }
    // Exclude null, undefined, false, etc. (conditional elements that don't render)
    return child !== null && child !== undefined;
  }).length;

  // Inject runRowStyle into all children
  const childrenWithStyle = Children.map(children, (child) => {
    // The type argument matters under React 19: isValidElement now narrows to
    // ReactElement<unknown>, so without it child.props is unknown and neither
    // the className read nor the spread below type-checks.
    if (isValidElement<{ className?: string }>(child)) {
      const existingClassName = child.props?.className || '';
      const newClassName = existingClassName ? `${runRowStyle} ${existingClassName}` : runRowStyle;

      return cloneElement(child, {
        ...child.props,
        className: newClassName,
      });
    }
    return child;
  });

  return (
    <div className={`cr-col-span-${childCount} cr-grid cr-grid-cols-subgrid ${className}`} onClick={onClick}>
      {childrenWithStyle}
    </div>
  );
}
